import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  Input,
  Text,
} from "@chakra-ui/react";
import { useColorModeValue } from "@chakra-ui/system";
import { Field, Formik } from "formik";
import * as Yup from "yup";
import DefaultAuth from "layouts/auth/Default";
import { axiosService } from "network/axios";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  setAlertMessageRequest,
  setAlertMessageSuccess,
} from "store/ui/reducer";

type ResetFormInputs = {
  password: string;
  confirmPassword: string;
};

const validationSchema = Yup.object({
  password: Yup.string()
    .min(8, "The password is less than 8 characters.")
    .matches(/[A-Z]/, "The password doesn't have a capital letter.")
    .matches(/\d/, "The password doesn't have any numbers.")
    .matches(/[!@#$%^&*(),.?":{}|<>]/, "The password doesn't have special characters.")
    .required("Password is required."),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password')], "The passwords don't match.")
    .required("Confirm Password is required."),
});

const inputStyle = {
  fontSize: "14px",
  lineHeight: "135%",
  textAlign: "left",
  color: "#999999",
  border: "none !important",
  boxShadow: "none !important",
  bgColor: "transparent",
};

const inputStyleNormal = {
  fontSize: "14px",
  lineHeight: "135%",
  textAlign: "left",
  color: "#0B0E10",
  border: "1px solid #E9E9E9",
  borderRadius: "12px",
  padding: "12px 16px",
  height: "48px",
  bgColor: "white",
  boxShadow: "none !important",
};

export default function Reset() {
  const initialValues: ResetFormInputs = {
    password: "",
    confirmPassword: "",
  };
  const queryParameters = new URLSearchParams(window.location.search);
  const email = queryParameters.get("email");
  const token = queryParameters.get("token");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const bgShadow = useColorModeValue(
    "0px 3.60347px 45.0434px rgba(0, 0, 0, 0.1)",
    "unset"
  );
  const textColor = useColorModeValue("navy.700", "white");
  const [isProcessing, setIsProcessing] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");
  const [isValid, setIsValid] = useState(false);

  // Custom password validation function that returns only errors
  const getPasswordErrors = (pwd: string, pwdAgain: string) => {
    const errors: string[] = [];
    
    if (pwd.length < 8) {
      errors.push("The password is less than 8 characters.");
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      errors.push("The password doesn't have special characters.");
    }
    if (!/\d/.test(pwd)) {
      errors.push("The password doesn't have any numbers.");
    }
    if (!/[A-Z]/.test(pwd)) {
      errors.push("The password doesn't have a capital letter.");
    }
    if (pwd.length > 0 && pwdAgain.length === 0) {
      errors.push("The passwords don't match.");
    } else if (pwdAgain.length > 0 && pwd !== pwdAgain) {
      errors.push("The passwords don't match.");
    }
    
    const allValid = errors.length === 0 && pwd.length > 0 && pwdAgain.length > 0 && pwd === pwdAgain;
    if (allValid !== isValid) {
      setIsValid(allValid);
    }
    
    return errors;
  };

  const onResetPassword = async (payload: any) => {
    if (!email && !token) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message:
            "Password Reset Link is wrong. Please check your email box again to get correct link",
        })
      );
      return;
    }

    const resetPayload = {
      password: payload.password,
      email: email,
      token: token,
    };

    try {
      await axiosService.post("/change", resetPayload);

      dispatch(
        setAlertMessageSuccess({
          type: "success",
          message: "Your password was updated successfully!",
        })
      );
      navigate("/auth/sign-in");
    } catch (error: any) {
      dispatch(
        setAlertMessageRequest({
          type: "error",
          message:
            error?.data?.messages?.error ||
            "Error occured in requesting password reset.",
        })
      );
      return;
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <DefaultAuth>
      <Flex
        maxW="453px"
        w="100%"
        mx="auto"
        my="auto"
        pt="40px"
        pb="10px"
        px="20px"
        h="fit-content"
        alignItems="start"
        justifyContent="center"
        flexDirection="column"
        borderRadius="16px"
        bgColor="#F5F7FA"
        border="1px solid #E6ECF3"
      >
        <Heading
          w="100%"
          color={textColor}
          fontFamily="General Sans"
          fontSize="24px"
          fontWeight="600"
          lineHeight="135%"
          textAlign="center"
        >
          Reset Password
        </Heading>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(values: any) => {
            onResetPassword(values);
          }}
        >
          {({ handleSubmit, errors, touched, values }) => (
            <form onSubmit={handleSubmit} style={{ width: "100%" }}>
              <Flex
                zIndex="2"
                direction="column"
                padding={{ md: "32px", base: "24px 20px" }}
                w="full"
              >
                <FormControl isInvalid={!!errors.password && touched.password}>
                  <Field name="password">
                    {({ field }: any) => (
                      <Input
                        {...field}
                        type="password"
                        id="password"
                        placeholder="Password"
                        sx={{
                          ...inputStyleNormal,
                          fontWeight: "400",
                        }}
                        _placeholder={{
                          ...inputStyle,
                          fontWeight: "400",
                        }}
                        _focus={{
                          border: "1px solid #E9E9E9",
                          boxShadow: "none",
                        }}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setPassword(e.target.value);
                        }}
                      />
                    )}
                  </Field>
                  <FormErrorMessage
                    mt="8px"
                    fontSize="13px"
                    color="#D32F2F"
                    bgColor="#FFE5E5"
                    padding="8px 12px"
                    borderRadius="6px"
                  >
                    {errors.password}
                  </FormErrorMessage>
                </FormControl>
                <FormControl
                  isInvalid={
                    !!errors.confirmPassword && touched.confirmPassword
                  }
                  mt="12px"
                >
                  <Field name="confirmPassword">
                    {({ field }: any) => (
                      <Input
                        {...field}
                        type="password"
                        id="confirmPassword"
                        placeholder="Confirm Password"
                        sx={{
                          ...inputStyleNormal,
                          fontWeight: "400",
                        }}
                        _placeholder={{
                          ...inputStyle,
                          fontWeight: "400",
                        }}
                        _focus={{
                          border: "1px solid #E9E9E9",
                          boxShadow: "none",
                        }}
                        onChange={(e: any) => {
                          field.onChange(e);
                          setPasswordAgain(e.target.value);
                        }}
                      />
                    )}
                  </Field>
                  <FormErrorMessage
                    mt="8px"
                    fontSize="13px"
                    color="#D32F2F"
                    bgColor="#FFE5E5"
                    padding="8px 12px"
                    borderRadius="6px"
                  >
                    {errors.confirmPassword}
                  </FormErrorMessage>
                </FormControl>
                {password && getPasswordErrors(password, passwordAgain).length > 0 && (
                  <Box
                    bg="#FEF2F2"
                    borderRadius="8px"
                    padding="12px 16px"
                    mt="14px"
                  >
                    <Text
                      fontFamily="General Sans"
                      fontSize="14px"
                      fontWeight="400"
                      color="#0B0E10"
                      mb="8px"
                    >
                      This password is invalid because:
                    </Text>
                    <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                      {getPasswordErrors(password, passwordAgain).map((error, index) => (
                        <li key={index} style={{ display: "flex", marginBottom: "4px" }}>
                          <span style={{ 
                            color: "#B91C1C", 
                            fontSize: "20px", 
                            marginRight: "12px",
                            lineHeight: "1.2"
                          }}>
                            •
                          </span>
                          <span style={{ 
                            fontFamily: "General Sans",
                            fontSize: "14px",
                            color: "#B91C1C",
                            flex: 1
                          }}>
                            {error}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Flex>
              <Flex
                flexDirection="row"
                justifyContent="center"
                alignItems="start"
                w="full"
                columnGap={"12px"}
                borderTop="1px solid #E9E9E9"
                padding={{ md: "24px 32px", base: "16px 20px" }}
              >
                <Button
                  fontSize="14px"
                  variant="outline"
                  bgColor="white"
                  color="#4285F4"
                  borderColor="#4285F4"
                  fontWeight="600"
                  padding="12px 24px"
                  h="48px"
                  w="100%"
                  borderRadius="24px"
                  onClick={() => {
                    navigate("/auth/sign-in");
                  }}
                  _hover={{
                    bgColor: "#4285F4",
                    color: "#FFF",
                  }}
                >
                  Back To Login
                </Button>
                <Button
                  type="submit"
                  fontSize="14px"
                  variant="solid"
                  bgColor="#4285F4"
                  color="white"
                  fontWeight="600"
                  padding="12px 24px"
                  h="48px"
                  w="100%"
                  borderRadius="24px"
                  isLoading={isProcessing}
                  isDisabled={!isValid}
                  _hover={{
                    bgColor: "#3367D6",
                  }}
                  _disabled={{
                    bgColor: "#E5E7EB",
                    color: "#9CA3AF",
                    cursor: "not-allowed",
                  }}
                >
                  Submit
                </Button>
              </Flex>
            </form>
          )}
        </Formik>
      </Flex>
    </DefaultAuth>
  );
}
